import { Point } from 'pixi.js'
import Layer from './index'
import { dimensions } from '@/scripts/config/settings'
import { LAYER_TINTS } from '@/scripts/config/constants'
import { addSirokoMorse } from '@/scripts/canvas/extra/Sirokko'
import { animateTleskacPath, createTleskacFromAtlas, tleskacPath } from '@/scripts/canvas/extra/Tleskac'
import { startTrafficSequence } from '@/scripts/canvas/extra/traffic'
import { addSmoke } from '@/scripts/canvas/extra/Smoke'
import { addLights } from '@/scripts/canvas/extra/lights'
import type { IItemInfo, ILayer } from '@/scripts/data/types'
import HiddenPlace, { HiddenPlaceRect } from '@/scripts/canvas/HiddenPlace/HiddenPlace'
import Snow from '@/scripts/canvas/extra/Snow'

type VehiclePoint = { id: string; x: number; y: number }

export function addExtraObjects(layer: Layer, layerData: ILayer, isLowEnd: boolean): void {
  const { extra } = layerData ?? {}

  switch (layer.name) {
    case 'layer_2': {
      addSmoke(layer, extra?.pozice_kominy ?? [], LAYER_TINTS.DEFAULT)
      addLights(layer, extra?.svetla_mesto ?? [])
      addHiddenPlaces(layer, extra?.skryta_mista ?? [])

      // Vehicles
      const passengerCar = extra?.auto?.find((e: VehiclePoint) => e.id === 'auto')
      const passengerCarWheel = extra?.auto?.find((e: VehiclePoint) => e.id === 'auto_kolo')
      const passengerCarLight = extra?.auto?.find((e: VehiclePoint) => e.id === 'auto_linear_light')
      const truckCar = extra?.nakladak?.find((e: VehiclePoint) => e.id === 'nakladak')
      const truckCarWheel = extra?.nakladak?.find((e: VehiclePoint) => e.id === 'nakladak_kolo')
      const truckCarSign = extra?.nakladak?.find((e: VehiclePoint) => e.id === 'nakladak_napis')
      const truckCarLight = extra?.nakladak?.find((e: VehiclePoint) => e.id === 'nakladak_linear_light')
      const tram = extra?.tramvaj?.find((e: VehiclePoint) => e.id === 'tramvaj')
      const tramGlow = extra?.tramvaj?.find((e: VehiclePoint) => e.id === 'tramvaj_linear_light')

      const tleskac = extra?.tleskac?.[0] as IItemInfo | undefined
      if (tleskac) {
        const { sprite } = createTleskacFromAtlas(layer, tleskac)
        const start = new Point(2100, 110)
        sprite.position.copyFrom(start)
        sprite.zIndex = 101 // Above extras
        sprite.visible = true

        animateTleskacPath(sprite, tleskacPath)
      }

      const sirokoDef = extra?.siroko?.[0] as IItemInfo | undefined
      if (sirokoDef) {
        addSirokoMorse(
          layer,
          sirokoDef,
          [
            'Jezka v kleci mam ja Sirokko',
            'Blizi se volba noveho Velkeho Vonta',
            'Zluta je barva nase',
            'Nevyzradme nic co vime',
            'Mlcenlivost chrani nas',
          ],
          {
            dot: 0.48,
            dash: 1.44,
            symbolGap: 0.48,
            letterGap: 1.44,
            wordGap: 3.36,
            messageGap: 10.0,
          },
          5.0,
        )
      }

      startTrafficSequence(layer, {
        passenger: { vehicle: passengerCar, light: passengerCarLight, wheel: passengerCarWheel, direction: 'right' },
        truck: {
          vehicle: truckCar,
          light: truckCarLight,
          wheel: truckCarWheel,
          sign: truckCarSign,
          direction: 'left',
        },
        tram: { vehicle: tram, light: tramGlow, direction: 'right' },
        travelDistance: 1200,
        pauseAfterPassengerCar: 1,
        pauseAfterTram: 10,
        pauseBeforeLoop: 1,
        tramStopHold: 4,
        passengerCarDriveDuration: 5,
        truckCarDriveDuration: 7,
        tramDriveDuration: 16,
      })
      break
    }

    case 'layer_3':
      addSmoke(layer, extra?.pozice_kominy ?? [], LAYER_TINTS.WHITE_ONLY)
      addHiddenPlaces(layer, extra?.skryta_mista ?? [])
      break

    case 'layer_4':
      addSmoke(layer, extra?.pozice_kominy ?? [], LAYER_TINTS.DEFAULT)
      addLights(layer, extra?.other ?? [])
      addHiddenPlaces(layer, extra?.skryta_mista ?? [])
      break

    case 'layer_5':
      layer.addExtra(new Snow(new Point(0, 0), dimensions.MIN_WIDTH, dimensions.MIN_HEIGHT, isLowEnd ? 0 : 1))
      addHiddenPlaces(layer, extra?.skryta_mista ?? [])
      break
  }
}

function addHiddenPlaces(layer: Layer, places: HiddenPlaceRect[] = []): void {
  for (const place of places) {
    layer.addExtra(new HiddenPlace(place), true)
  }
}
